import { createContext, useContext, useEffect, useState } from 'react';
import { fetchCurrentUser, loginUser, logoutUser, signupUser } from '../services/auth';

const AuthContext = createContext(null);

const readStoredUser = () => {
  try {
    const raw = localStorage.getItem('auth.user');
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.warn('Unable to read stored user', error);
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => localStorage.getItem('auth.loggedIn') === 'true'
  );
  const [user, setUser] = useState(readStoredUser);
  const [accessToken, setAccessToken] = useState(() => localStorage.getItem('auth.accessToken') || '');

  const persistUser = (payload) => {
    if (payload?.user) {
      setUser(payload.user);
      localStorage.setItem('auth.user', JSON.stringify(payload.user));
    }
  };

  const persistTokens = (payload) => {
    const token = payload?.session?.access_token;
    if (token) {
      setAccessToken(token);
      localStorage.setItem('auth.accessToken', token);
    }
    const refresh = payload?.session?.refresh_token;
    if (refresh) {
      localStorage.setItem('auth.refreshToken', refresh);
    }
  };

  const handleLogin = async (email, password) => {
    const data = await loginUser(email, password);
    setIsAuthenticated(true);
    localStorage.setItem('auth.loggedIn', 'true');
    persistUser(data);
    persistTokens(data);
    return data;
  };

  const handleSignup = async (payload) => {
    const data = await signupUser(payload);
    setIsAuthenticated(true);
    localStorage.setItem('auth.loggedIn', 'true');
    persistUser(data);
    persistTokens(data);
    return data;
  };

  const handleLogout = async () => {
    await logoutUser(accessToken);
    setIsAuthenticated(false);
    setUser(null);
    setAccessToken('');
    localStorage.removeItem('auth.loggedIn');
    localStorage.removeItem('auth.user');
    localStorage.removeItem('auth.accessToken');
    localStorage.removeItem('auth.refreshToken');
  };

  const setSessionFromToken = async (token) => {
    if (!token) return;
    const profile = await fetchCurrentUser(token);
    setIsAuthenticated(true);
    localStorage.setItem('auth.loggedIn', 'true');
    setUser(profile);
    localStorage.setItem('auth.user', JSON.stringify(profile));
    setAccessToken(token);
    localStorage.setItem('auth.accessToken', token);
  };

  // If we have no token, consider the session logged out.
  useEffect(() => {
    if (!accessToken) {
      setIsAuthenticated(false);
      setUser(null);
      localStorage.removeItem('auth.loggedIn');
      localStorage.removeItem('auth.user');
    }
  }, [accessToken]);

  // Validate stored token on mount/change; if expired/invalid, force logout.
  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    (async () => {
      try {
        const profile = await fetchCurrentUser(accessToken);
        if (cancelled) return;
        setIsAuthenticated(true);
        setUser(profile);
        localStorage.setItem('auth.loggedIn', 'true');
        localStorage.setItem('auth.user', JSON.stringify(profile));
      } catch (error) {
        if (cancelled) return;
        console.warn('Token validation failed, logging out', error);
        setIsAuthenticated(false);
        setUser(null);
        setAccessToken('');
        localStorage.removeItem('auth.loggedIn');
        localStorage.removeItem('auth.user');
        localStorage.removeItem('auth.accessToken');
        localStorage.removeItem('auth.refreshToken');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        accessToken,
        login: handleLogin,
        signup: handleSignup,
        logout: handleLogout,
        setSessionFromToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
